import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSigningPinStatusRequest } from 'src/redux/actions/signing-pin-action';
import { useFeatureToggleWhenReady } from './use-feature-toggle';

//  Single epic-named Split flag, keyed on the TENANT (ENTERPRISE). Matches backend tenant-keying.
export const SIGNATURE_VALIDATION_FLAG = 'THIN-7681-signature-validation';

//  One place to answer "is signature-validation on, and does this user have a PIN yet?".
//
//  We key `enabled` off the BACKEND's `toggleOn` (from /signing-pin/status), NOT the client-side
//  Split treatment. Reason: the FE's own tenant resolution (getRealmName) is unreliable for some
//  sessions — on localhost it falls back to the URL path when localStorage 'realm' is unset, so a
//  physician hard-reloading on /physician-dashboard keys the Split SDK on "physician-dashboard"
//  instead of the tenant, and the flag resolves off. The backend resolves the tenant the same way
//  the sign endpoints do, so `toggleOn` never disagrees with what those endpoints enforce. We fetch
//  status once per session so every user (physicians included) gets the authoritative answer.
export function useSignatureValidation() {
  const dispatch = useDispatch();
  //  Read the client-side treatment only to surface loading state; it is NOT the gate.
  const [enabledRaw] = useFeatureToggleWhenReady(SIGNATURE_VALIDATION_FLAG, 'ENTERPRISE');
  const signingPin = useSelector((state: any) => state.signingPinReducer);

  useEffect(() => {
    //  THIN-7788: also gate on statusError so a failed fetch doesn't re-dispatch forever.
    //  On failure we fall back to a safe default (validation disabled); the backend still
    //  enforces the PIN at sign time, so this never bypasses the gate.
    if (!signingPin?.status && !signingPin?.statusLoading && !signingPin?.statusError) {
      dispatch(getSigningPinStatusRequest());
    }
  }, [signingPin?.status, signingPin?.statusLoading, signingPin?.statusError, dispatch]);

  return {
    enabled: signingPin?.status?.toggleOn === true,
    flagLoading:
      !signingPin?.status &&
      !signingPin?.statusError &&
      (enabledRaw === null || !!signingPin?.statusLoading),
    hasPin: signingPin?.status?.hasPin === true,
    statusLoading: !!signingPin?.statusLoading,
    status: signingPin?.status ?? null,
  };
}

export default useSignatureValidation;
